// src/lib/apiClient.js
// Cookie-based BFF client (same-origin /api proxy).
// Server sets:
//  - HttpOnly cookie "bff_session" (unreadable to JS)
//  - Readable cookie "csrf" for anti-CSRF header
// Enhancement: optionally send a non-sensitive pseudonymous *hint* header (X-Pseudo-UID)
// so the server can fold it into a server-side HMAC for a stable, pseudonymous ID.
// The server remains authoritative and never trusts this hint directly.

function getCookie(name) {
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return m ? decodeURIComponent(m[1]) : null;
}

async function seedCsrf() {
  // Idempotent: server should set CSRF cookie if missing
  await fetch('/api/health', { credentials: 'include' });
  return getCookie('csrf');
}

// ---- OPTIONAL PSEUDO UID HINT SUPPORT ----
let pseudoUidHint = null;

/**
 * Provide an optional, non-sensitive pseudonymous ID hint (e.g., from buildPseudonymousId()).
 * The server will treat this as an *untrusted* salt only. Max 128 chars are sent.
 */
export function setPseudoUidHint(val) {
  if (!val) {
    pseudoUidHint = null;
    return;
  }
  const s = String(val);
  pseudoUidHint = s.slice(0, 128);
}

function withDefaultHeaders(extra = {}) {
  return pseudoUidHint
    ? { ...extra, 'X-Pseudo-UID': pseudoUidHint }
    : extra;
}

/** Normalize any caller path to `/api/...` and remove duplicate slashes. */
function toApiPath(path) {
  let p = String(path || '');
  if (!p.startsWith('/')) p = '/' + p;
  if (!p.startsWith('/api/')) p = '/api' + (p.startsWith('/api') ? '' : p);
  // collapse multiple slashes except after 'https?:'
  p = p.replace(/([^:]\/)\/+/g, '$1');
  return p;
}

let bootPromise = null;

/**
 * Bootstrap an anon BFF session (idempotent).
 * Called once on app start, and also auto-called before GET/POST/PUT/PATCH/DELETE.
 */
export async function bootstrapBffSession({ force = false } = {}) {
  // If not forcing, use existing session if the bff_session cookie exists.
  if (!force) {
    if (getCookie('bff_session')) {
      // Ensure CSRF exists (helpful for immediate POST after boot)
      if (!getCookie('csrf')) await seedCsrf().catch(() => {});
      return getCookie('csrf');
    }
    if (bootPromise) return bootPromise;
  }

  bootPromise = (async () => {
    const csrf = await seedCsrf().catch(() => null);
    const r = await fetch('/api/auth/anon', {
      method: 'POST',
      credentials: 'include',
      headers: withDefaultHeaders({ 'X-CSRF': csrf || '' }),
    });

    // Accept 204 No Content or any 2xx OK
    if (!(r.status === 204 || r.ok)) {
      const ct = r.headers.get('content-type') || '';
      const body = ct.includes('application/json') ? await r.json() : await r.text();
      const msg = typeof body === 'string' ? body : body?.error || 'Anon auth failed';
      throw new Error(msg);
    }

    return getCookie('csrf');
  })();

  try {
    return await bootPromise;
  } finally {
    bootPromise = null;
  }
}

async function parseResponse(r) {
  const ct = r.headers.get('content-type') || '';
  const body = ct.includes('application/json') ? await r.json() : await r.text();
  if (!r.ok) {
    const msg =
      typeof body === 'string'
        ? body
        : body?.message || body?.error || r.statusText;
    const hasObjectPayload = body && typeof body === 'object' && !Array.isArray(body);
    const code = hasObjectPayload ? body.code : undefined;
    const error = { status: r.status, msg, code };
    if (hasObjectPayload) {
      error.data = body;
      if (body.expiresAt) error.expiresAt = body.expiresAt;
      if (body.expiresAt) error.expiresAt = body.expiresAt;
    }
    throw error;
  }
  return body;
}

/** Core fetch with session + CSRF handling and one retry for 401/403. */
async function request(method, path, body, options = {}) {
  const url = toApiPath(path);
  const { headers: extraHeaders = {}, signal, rawResponse = false } = options || {};

  // Ensure session before auth-required reads/writes
  let csrf = getCookie('csrf') || await bootstrapBffSession();

  const buildHeaders = () => {
    const base =
      method === 'GET'
        ? {}
        : { 'Content-Type': 'application/json', 'X-CSRF': csrf || '' };
    return withDefaultHeaders({ ...base, ...extraHeaders });
  };

  const encodeBody = () => {
    if (method === 'GET' || body === undefined || body === null) {
      return undefined;
    }
    if (body instanceof FormData || body instanceof Blob) {
      return body;
    }
    return JSON.stringify(body);
  };

  const doFetch = async () =>
    fetch(url, {
      method,
      credentials: 'include',
      headers: buildHeaders(),
      body: encodeBody(),
      signal,
    });

  let r = await doFetch();

  // One retry path: 403 (CSRF) → reseed; 401 (session) → force re-bootstrap.
  if (r.status === 403 && method !== 'GET') {
    csrf = await seedCsrf();
    r = await doFetch();
  } else if (r.status === 401) {
    csrf = await bootstrapBffSession({ force: true });
    r = await doFetch();
  }

  if (rawResponse) {
    return r;
  }

  return parseResponse(r);
}

/** GET */
export async function apiGet(path, options) {
  return request('GET', path, undefined, options);
}

/** POST */
export async function apiPost(path, body, options) {
  return request('POST', path, body, options);
}

/** PUT */
export async function apiPut(path, body, options) {
  return request('PUT', path, body, options);
}

/** PATCH */
export async function apiPatch(path, body, options) {
  return request('PATCH', path, body, options);
}

/** DELETE */
export async function apiDelete(path, body, options) {
  // Some servers accept a JSON body for DELETE; send it only if provided.
  return request('DELETE', path, body, options);
}
